import { z } from 'zod';
import { labelFieldType } from './labels';

export const formVariableType = z.enum(['String', 'Integer']);

export const knownFormConditionType = z.enum([
  'equals',
  'not_equals',
  'greater_than',
  'greater_than_or_equal',
  'less_than',
  'less_than_or_equal',
  'contains',
  'matches_regex',
  'expression',
]);

export const knownFormConditionOperator = z.enum([
  '==',
  '!=',
  '>',
  '>=',
  '<',
  '<=',
  'contains',
  'regex',
  'starts_with',
  'ends_with',
  'in',
  'not_in',
  'is_null',
  'is_not_null',
]);

export const knownFormActionType = z.enum([
  'block',
  'hide',
  'show',
  'set_value',
  'normalize',
  'disable',
  'readonly',
  'counter',
]);

/**
 * Config schema for the `counter` action.
 * The action populates `field` with a sequential value reserved at print time.
 * The counter starts at `start` and rolls over back to `start` when it reaches `max`.
 */
export const counterActionConfig = z.object({
  field: z.string().min(1),
  start: z.number().int().min(0).default(0),
  step: z.number().int().min(1).default(1),
  max: z.number().int().min(1).default(999999),
  length: z.number().int().min(1).optional(),
  padChar: z.string().length(1).default('0'),
  prefix: z.string().default(''),
  suffix: z.string().default(''),
  rollover: z.boolean().default(true),
});
export const formConditionType = knownFormConditionType;
export const formConditionOperator = knownFormConditionOperator;
export const formActionType = knownFormActionType;

export const formVariableRule = z.string().nullable().optional();

export const formFieldConstraints = z.object({
  integerOnly: z.boolean().optional(),
  maxDecimals: z.number().int().min(0).optional(),
  regex: z.string().optional(),
  minLength: z.number().int().min(0).optional(),
  maxLength: z.number().int().min(0).optional(),
  allowedValues: z.array(z.string()).optional(),
  minValue: z.number().optional(),
  maxValue: z.number().optional(),
});

export const formField = z.object({
  id: z.number().optional(),
  formId: z.number().optional(),
  name: z.string(),
  type: labelFieldType,
  required: z.boolean().optional(),
  allowedCharacters: z.string().nullable().optional(),
  length: z.number().int().nullable().optional(),
  fixedLength: z.boolean().nullable().optional(),
  constraints: formFieldConstraints.nullable().optional(),
  pickListValues: z.array(z.string()).nullable().optional(),
  minimumValue: z.string().nullable().optional(),
  maximumValue: z.string().nullable().optional(),
  order: z.number().int().optional(),
});

export const formVariable = z.object({
  id: z.number().optional(),
  formId: z.number().optional(),
  name: z.string(),
  labelFieldMapping: z.string(),
  type: formVariableType,
  variableRule: formVariableRule,
});

export const formCondition = z.object({
  id: z.number().optional(),
  ruleId: z.number().optional(),
  type: formConditionType,
  operator: formConditionOperator.optional(),
  value: z.unknown().optional(),
});

export const formAction = z.object({
  id: z.number().optional(),
  ruleId: z.number().optional(),
  type: formActionType,
  config: z.unknown().optional(),
});

export const formRule = z.object({
  id: z.number().optional(),
  formId: z.number().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  enabled: z.boolean().optional(),
  priority: z.number().int().optional(),
  conditions: z.array(formCondition).optional().default([]),
  actions: z.array(formAction).optional().default([]),
});

export const form = z.object({
  id: z.number().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  labelId: z.number(),
  printerIds: z.array(z.number()).optional().default([]),
  fields: z.array(formField).optional().default([]),
  variables: z.array(formVariable).optional().default([]),
  rules: z.array(formRule).optional().default([]),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  createdBy: z.string(),
});

export type Form = z.infer<typeof form>;
export type FormField = z.infer<typeof formField>;
export type FormFieldConstraints = z.infer<typeof formFieldConstraints>;
export type FormVariable = z.infer<typeof formVariable>;
export type FormVariableType = z.infer<typeof formVariableType>;
export type FormVariableRule = z.infer<typeof formVariableRule>;
export type FormConditionType = z.infer<typeof formConditionType>;
export type FormConditionOperator = z.infer<typeof formConditionOperator>;
export type FormActionType = z.infer<typeof formActionType>;
export type FormRule = z.infer<typeof formRule>;
export type FormCondition = z.infer<typeof formCondition>;
export type FormAction = z.infer<typeof formAction>;
export type CounterActionConfig = z.infer<typeof counterActionConfig>;
